import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { makeStyles } from "@material-ui/styles";
import { 
    Grid, 
    Typography, 
    TextField, 
    FormControlLabel, 
    Checkbox,
    Divider,
    Button,
    Paper,
    Card,
    CardHeader,
    CardContent
} from "@material-ui/core";
import { ValidatorForm } from "react-material-ui-form-validator";
import {
    Page,
    PageContent,
    FormActions,
    FormButton,
    FormSubmitButton,
    ScrollToTopOnMount,
    SearchDropdown,
    RadioListInput
} from "frame/components";
import { PageHeaderTabs } from "features/health-safety-forms/components";
import { useRouter } from "utils";
import * as fromHealthAndSafety from "features/health-and-safety/actions/types";
import uuid from "uuid";
import { onSiteInspectionChecklistCreateRequest } from "features/health-and-safety/actions";
import { SiteForm } from "features/forms";

const useStyles = makeStyles((theme) => ({
    root: {
        width: "100%",
    },
    actions: {
        marginTop: theme.spacing(2),
    },
    error: {
        color: theme.palette.error.main,
    },
    section: {
        marginBottom: theme.spacing(4),
    },
    sectionTitle: {
        marginBottom: theme.spacing(2),
        fontWeight: "bold",
    },
    subsection: {
        marginTop: theme.spacing(2),
        marginBottom: theme.spacing(2),
    },
    divider: {
        margin: theme.spacing(3, 0),
    },
    fieldGroup: {
        marginBottom: theme.spacing(2),
    },
    remedialActions: {
        marginTop: theme.spacing(2),
        padding: theme.spacing(2),
        backgroundColor: theme.palette.background.default,
        marginBottom: theme.spacing(2),
    },
    remedialAction: {
        marginBottom: theme.spacing(2),
    },
    addButton: {
        marginTop: theme.spacing(1),
    },
    card: {
        margin: theme.spacing(2, 0),
        padding: theme.spacing(2)
    }
}));

const SiteInspectionChecklistForm = ({ values, onChange, onReset, onSave, onGetSiteInspectionChecklistList }) => {
    const classes = useStyles();
    const { history } = useRouter();
    const [formData, setFormData] = useState({
        id: uuid.v4(),
        name: "Site Inspection Checklist - Generic",
        siteId: "",
        location: "",
        inspector: "",
        date: new Date().toISOString().substring(0, 10),
        time: new Date().toTimeString().substring(0, 5),
        
        // All checklist fields initialized to empty string instead of false
        hazardBoardSignage: "",
        environmentalPlanIssues: "",
        toolboxTalk: "",
        toolboxTalkDate: "",
        safetyInductions: "",
        safetyNoticeBoard: "",
        
        // Site Facilities
        officesClean: "",
        smokoShedsClean: "",
        toiletsClean: "",
        toolEquipmentShedsAdequate: "",
        
        // General Site Tidiness & Access Ways
        clearSafeAccess: "",
        stairwaysAccessWaysClear: "",
        hoardingsFenceSecure: "",
        looseMaterialsSecure: "",
        
        // Personal Safety Equipment
        signageDisplayedLegible: "",
        hardhatsWorn: "",
        correctFootwear: "",
        glassesEarMuffsVestsMasks: "",
        
        // First Aid / Fire Prevention
        firstAidBox: "",
        accidentRegister: "",
        fireExtinguishersAvailable: "",
        fireExtinguishersCurrent: "",
        fireExtinguishersSufficientNumber: "",
        evacuationProcedureCurrent: "",
        allEmergenciesIncluded: "",
        
        // Remedial Actions - start with empty array
        remedialActions: []
    });

    // Update the local form data when the values prop changes
    useEffect(() => {
        if (values) {
            setFormData(values);
        }
    }, [values]);

    useEffect(() => {
        console.log("DEBUG - Form component mounted, fetching list");
        onGetSiteInspectionChecklistList();
    }, [onGetSiteInspectionChecklistList]);

    const onSubmit = (event) => {
        event.preventDefault();
        
        // Ensure time is set to current time
        const submissionData = {
            ...formData,
            time: new Date().toTimeString().substring(0, 5) // Ensure time is always set to current time
        };
        
        console.log("Form submitted with data:", submissionData);
        
        // Save to state/store
        if (onSave) {
            onSave(submissionData);
        }
        
        // Then dispatch the create request to the API
        // This is what actually saves to the backend
        if (onGetSiteInspectionChecklistList) {
            onGetSiteInspectionChecklistList(submissionData);
        }
    };

    const onCancel = () => {
        console.log("DEBUG - Form cancelled");
        onReset();
        history.push(fromHealthAndSafety.INFO_ROUTE);
    };

    const handleTextChange = (field) => (event) => {
        const newFormData = {
            ...formData,
            [field]: event.target.value,
        };
        setFormData(newFormData);
        onChange(newFormData);
    };

    const handleRadioChange = (field) => (value) => {
        const newFormData = {
            ...formData,
            [field]: value,
        };
        setFormData(newFormData);
        onChange(newFormData);
    };

    const handleDateChange = (field) => (event) => {
        const newFormData = {
            ...formData,
            [field]: event.target.value,
        };
        setFormData(newFormData);
        onChange(newFormData);
    };

    const handleRemedialActionChange = (index, field) => (event) => {
        const value = field === 'completed' ? event.target.checked : event.target.value;
        const newRemedialActions = [...formData.remedialActions];
        newRemedialActions[index] = {
            ...newRemedialActions[index],
            [field]: value
        };
        
        const newFormData = {
            ...formData,
            remedialActions: newRemedialActions
        };
        setFormData(newFormData);
        onChange(newFormData);
    };

    const addRemedialAction = () => {
        // Only add one remedial action if none exist
        if (formData.remedialActions.length === 0) {
            const newRemedialActions = [
                {
                    action: "",
                    comments: "",
                    person: "",
                    completed: false
                }
            ];
            
            const newFormData = {
                ...formData,
                remedialActions: newRemedialActions
            };
            setFormData(newFormData);
            onChange(newFormData);
        }
    };

    const removeRemedialAction = (index) => {
        const newRemedialActions = [...formData.remedialActions];
        newRemedialActions.splice(index, 1);
        
        const newFormData = {
            ...formData,
            remedialActions: newRemedialActions
        };
        setFormData(newFormData);
        onChange(newFormData);
    };

    const handleSiteChange = (site) => {
        console.log("DEBUG - Site selected:", site);
        if (site && site.id) {
            const newFormData = {
                ...formData,
                siteId: site.id
            };
            setFormData(newFormData);
            onChange(newFormData);
        }
    };

    // Options for Yes/No/NA radio buttons
    const options = [
        {
            label: "Yes",
            value: "yes",
        },
        {
            label: "No",
            value: "no",
        },
        {
            label: "NA",
            value: "na",
        },
    ];

    return (
        <Page>
            <ScrollToTopOnMount />
            <PageHeaderTabs />
            <PageContent>
                <ValidatorForm onSubmit={onSubmit} className={classes.root}>
                    <Typography variant="h5" gutterBottom>
                        Add Site Inspection Checklist
                    </Typography>
                    
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography variant="h6" className={classes.sectionTitle}>
                                Basic Information
                            </Typography>
                            
                            <Grid container spacing={3}>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Checklist Name"
                                        variant="outlined"
                                        value={formData.name}
                                        onChange={handleTextChange('name')}
                                        margin="normal"
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <SiteForm
                                        onSiteSelected={handleSiteChange}
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Inspector"
                                        variant="outlined"
                                        value={formData.inspector}
                                        onChange={handleTextChange('inspector')}
                                        margin="normal"
                                    />
                                </Grid>
                                
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        fullWidth
                                        label="Date"
                                        type="date"
                                        variant="outlined"
                                        value={formData.date}
                                        onChange={handleDateChange('date')}
                                        margin="normal"
                                        InputLabelProps={{
                                            shrink: true,
                                        }}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    
                    {/* Add more sections for each checklist category */}
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography variant="h6" className={classes.sectionTitle}>
                                1. Site Control
                            </Typography>
                            
                            <Grid container spacing={2}>
                                <Grid item xs={12}>
                                    <Typography align="left" gutterBottom variant="body1">
                                        Hazard Board Signage
                                    </Typography>
                                    <RadioListInput
                                        data-cy="hazardBoardSignage"
                                        initialValue={formData.hazardBoardSignage}
                                        options={options}
                                        onChange={handleRadioChange('hazardBoardSignage')}
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Typography align="left" gutterBottom variant="body1">
                                        Environmental Plan Issues
                                    </Typography>
                                    <RadioListInput
                                        data-cy="environmentalPlanIssues"
                                        initialValue={formData.environmentalPlanIssues}
                                        options={options}
                                        onChange={handleRadioChange('environmentalPlanIssues')}
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Typography align="left" gutterBottom variant="body1">
                                        Toolbox Talk
                                    </Typography>
                                    <RadioListInput
                                        data-cy="toolboxTalk"
                                        initialValue={formData.toolboxTalk}
                                        options={options}
                                        onChange={handleRadioChange('toolboxTalk')}
                                    />
                                </Grid>
                                
                                {formData.toolboxTalk === "yes" && (
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            fullWidth
                                            label="Toolbox Talk Date"
                                            type="date"
                                            variant="outlined"
                                            value={formData.toolboxTalkDate}
                                            onChange={handleDateChange('toolboxTalkDate')}
                                            InputLabelProps={{
                                                shrink: true,
                                            }}
                                        />
                                    </Grid>
                                )}
                                
                                <Grid item xs={12}>
                                    <Typography align="left" gutterBottom variant="body1">
                                        Safety Inductions
                                    </Typography>
                                    <RadioListInput
                                        data-cy="safetyInductions"
                                        initialValue={formData.safetyInductions}
                                        options={options}
                                        onChange={handleRadioChange('safetyInductions')}
                                    />
                                </Grid>
                                
                                <Grid item xs={12}>
                                    <Typography align="left" gutterBottom variant="body1">
                                        Safety Notice Board
                                    </Typography>
                                    <RadioListInput
                                        data-cy="safetyNoticeBoard"
                                        initialValue={formData.safetyNoticeBoard}
                                        options={options}
                                        onChange={handleRadioChange('safetyNoticeBoard')}
                                    />
                                </Grid>
                            </Grid>
                        </CardContent>
                    </Card>
                    
                    {/* Additional sections would follow the same pattern */}
                    
                    {/* Remedial Actions */}
                    <Card className={classes.card}>
                        <CardContent>
                            <Typography variant="h6" className={classes.sectionTitle}>
                                Remedial Actions
                            </Typography>
                            
                            {formData.remedialActions.map((action, index) => (
                                <Paper key={index} className={classes.remedialActions}>
                                    <Grid container spacing={2}>
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Action"
                                                variant="outlined"
                                                value={action.action}
                                                onChange={handleRemedialActionChange(index, 'action')}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <TextField
                                                fullWidth
                                                label="Comments"
                                                variant="outlined"
                                                value={action.comments}
                                                onChange={handleRemedialActionChange(index, 'comments')}
                                                multiline
                                                rows={2}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12} md={6}>
                                            <TextField
                                                fullWidth
                                                label="Person Responsible"
                                                variant="outlined"
                                                value={action.person}
                                                onChange={handleRemedialActionChange(index, 'person')}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Typography align="left" gutterBottom variant="body1">
                                                Completed
                                            </Typography>
                                            <RadioListInput
                                                data-cy={`remedialAction-${index}-completed`}
                                                initialValue={action.completed ? "yes" : "na"}
                                                options={options}
                                                onChange={(value) => {
                                                    const newRemedialActions = [...formData.remedialActions];
                                                    newRemedialActions[index] = {
                                                        ...newRemedialActions[index],
                                                        completed: value === "yes"
                                                    };
                                                    
                                                    const newFormData = {
                                                        ...formData,
                                                        remedialActions: newRemedialActions
                                                    };
                                                    setFormData(newFormData);
                                                    onChange(newFormData);
                                                }}
                                            />
                                        </Grid>
                                        
                                        <Grid item xs={12}>
                                            <Button
                                                variant="outlined"
                                                color="secondary"
                                                onClick={() => removeRemedialAction(index)}
                                            >
                                                Remove
                                            </Button>
                                        </Grid>
                                    </Grid>
                                </Paper>
                            ))}
                            
                            <Button
                                variant="contained"
                                color="primary"
                                onClick={addRemedialAction}
                                className={classes.addButton}
                            >
                                Add Remedial Action
                            </Button>
                        </CardContent>
                    </Card>
                    
                    <FormActions>
                        <FormButton variant="outlined" onClick={onCancel}>
                            Cancel
                        </FormButton>
                        <FormSubmitButton>Save</FormSubmitButton>
                    </FormActions>
                </ValidatorForm>
            </PageContent>
        </Page>
    );
};

SiteInspectionChecklistForm.propTypes = {
    values: PropTypes.object,
    onChange: PropTypes.func.isRequired,
    onReset: PropTypes.func.isRequired,
    onSave: PropTypes.func.isRequired,
    onGetSiteInspectionChecklistList: PropTypes.func.isRequired
};

export default SiteInspectionChecklistForm; 